import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { apiService, Article, resolveStrapiMediaUrl } from '../services/api';
import BlocksRenderer from '../components/BlocksRenderer';
import colors from '../theme/colors';

const ArticleContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  background-color: ${colors.mainWhite};
`;

const BackLink = styled(Link)`
  color: ${colors.mainOrange};
  text-decoration: none;
  font-weight: 500;
  margin-bottom: 1rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: color 0.3s ease;

  &:hover {
    color: ${colors.mainOrangeLight};
    text-decoration: none;
  }
`;

const ArticleImage = styled.div<{ imageUrl?: string }>`
  height: 400px;
  background-image: url(${props => props.imageUrl || '/api/placeholder/800/400'});
  background-size: cover;
  background-position: center;
  background-color: ${colors.goldGrey};
  border-radius: 8px;
  margin-bottom: 2rem;
  box-shadow: 0 4px 12px rgba(30, 30, 30, 0.1);
`;

const ArticleTitle = styled.h1`
  font-size: clamp(1.75rem, 4vw, 2.25rem);
  margin: 0 0 0.75rem 0;
  color: ${colors.mainBlack};
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.25;
  background: ${colors.mainGradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const ArticleMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid ${colors.goldGrey};
`;

const ArticleAuthor = styled.div`
  font-weight: 500;
  color: ${colors.mainBlack};
`;

const ArticleDate = styled.div`
  color: ${colors.heavyGray};
  font-size: 0.9rem;
`;

const ArticleContent = styled.div`
  font-size: 1.0625rem;
  line-height: 1.7;
  color: ${colors.textDark};

  p {
    margin-bottom: 1.25rem;
  }

  h1 { font-size: 2rem; margin: 2rem 0 0.75rem 0; font-weight: 700; }
  h2 { font-size: 1.5rem; margin: 1.75rem 0 0.6rem 0; font-weight: 600; color: ${colors.mainBlack}; }
  h3 { font-size: 1.25rem; margin: 1.5rem 0 0.5rem 0; font-weight: 600; color: ${colors.mainBlack}; }
  h4, h5, h6 { font-size: 1.125rem; margin: 1.25rem 0 0.5rem 0; font-weight: 600; }
`;

const ArticleBottom = styled.div`
  margin-top: 2rem;
  padding-top: 1.25rem;
  border-top: 1px solid ${colors.goldGrey};
  display: grid;
  gap: 1rem;
`;

const BottomLabel = styled.h4`
  margin: 0 0 0.5rem 0;
  font-size: 0.95rem;
  color: ${colors.mainBlack};
`;

const AuthorLink = styled(Link)`
  color: ${colors.mainOrange};
  text-decoration: none;
  font-weight: 600;

  &:hover {
    color: ${colors.mainOrangeLight};
    text-decoration: underline;
  }
`;

const TagsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const TagLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  background: ${colors.goldGrey};
  color: ${colors.mainBlack};
  text-decoration: none;
  font-size: 0.85rem;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    background: ${colors.mainOrange};
    color: ${colors.mainWhite};
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 3rem;
  font-size: 1.1rem;
  color: ${colors.textGray};
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${colors.mainOrange};
  background-color: ${colors.goldGrey};
  border: 2px solid ${colors.mainOrange};
  border-radius: 8px;
  margin: 2rem 0;
`;

const ArticleDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchArticle = async () => {
      if (!slug) return;

      try {
        setLoading(true);
        setError(null);

        const articleId = parseInt(slug);
        let response;

        if (!isNaN(articleId) && slug === articleId.toString()) {
          response = await apiService.getArticleById(articleId);
        } else {
          response = await apiService.getArticleBySlug(slug);
        }

        if (response.data && (response.data.title || response.data.slug)) {
          setArticle(response.data);
          setError(null);
        } else {
          setError(`Статтю не знайдено. Slug: ${slug}`);
        }
      } catch (err: any) {
        setError(`Не вдалося завантажити статтю. Помилка: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [slug]);

  if (loading) {
    return (
      <ArticleContainer>
        <LoadingMessage>Завантаження статті...</LoadingMessage>
      </ArticleContainer>
    );
  }

  if (error) {
    return (
      <ArticleContainer>
        <ErrorMessage>{error}</ErrorMessage>
      </ArticleContainer>
    );
  }

  if (!article || !article.title) {
    return (
      <ArticleContainer>
        <ErrorMessage>Статтю не знайдено</ErrorMessage>
      </ArticleContainer>
    );
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('uk-UA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getImageUrl = () => {
    if (article.cover?.url) {
      return resolveStrapiMediaUrl(article.cover.url);
    }
    return undefined;
  };

  const tagItems = Array.isArray(article.tags) ? article.tags.filter((t) => t?.name) : [];
  const authorSlug = article.author?.slug || (article.author?.id ? String(article.author.id) : '');

  return (
    <ArticleContainer>
      <BackLink to="/">← На головну</BackLink>

      <ArticleImage imageUrl={getImageUrl()} />

      <ArticleTitle>{article.title}</ArticleTitle>

      <ArticleMeta>
        <ArticleAuthor>
          {article.author?.name || 'Невідомий автор'}
        </ArticleAuthor>
        <ArticleDate>{formatDate(article.publishedAt)}</ArticleDate>
      </ArticleMeta>

      <ArticleContent>
        {article.blocks && article.blocks.length > 0 ? (
          <BlocksRenderer blocks={article.blocks} />
        ) : (
          <p>Для цієї статті поки немає контенту.</p>
        )}
      </ArticleContent>

      <ArticleBottom>
        {authorSlug && (
          <div>
            <BottomLabel>Автор</BottomLabel>
            <AuthorLink to={`/author/${encodeURIComponent(authorSlug)}`}>
              {article.author?.name || 'Невідомий автор'}
            </AuthorLink>
          </div>
        )}

        {tagItems.length > 0 && (
          <div>
            <BottomLabel>Хештеги</BottomLabel>
            <TagsRow>
              {tagItems.map((tag, idx) => (
                <TagLink key={`${tag.name}-${idx}`} to={`/tag/${encodeURIComponent(tag.name)}`}>
                  #{tag.name}
                </TagLink>
              ))}
            </TagsRow>
          </div>
        )}
      </ArticleBottom>
    </ArticleContainer>
  );
};

export default ArticleDetail;
